import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Spinner, TextField, Typography } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import {
  adminApiClient,
  type RunnerAppStatus,
  type RunnerCallEvent,
  type RunnerInsurancePolicy,
  type RunnerVerificationCall,
} from '../lib/adminApiClient';

const STATUS_CHIP: Record<string, { label: string; fg: string; bg: string }> = {
  pending: { label: 'Waiting for review', fg: '#b45309', bg: '#fef3c7' },
  approved: { label: 'Approved', fg: '#15803d', bg: '#dcfce7' },
  rejected: { label: 'Sent back', fg: '#b91c1c', bg: '#fee2e2' },
  superseded: { label: 'Replaced', fg: '#64748b', bg: '#f1f5f9' },
  scheduled: { label: 'Booked', fg: '#1d4ed8', bg: '#dbeafe' },
  completed: { label: 'Completed', fg: '#15803d', bg: '#dcfce7' },
  canceled: { label: 'Canceled', fg: '#64748b', bg: '#f1f5f9' },
  rescheduled: { label: 'Rescheduled', fg: '#64748b', bg: '#f1f5f9' },
  no_show: { label: 'No-show', fg: '#b91c1c', bg: '#fee2e2' },
};

function StatusChip({ status }: { status: string }) {
  const c = STATUS_CHIP[status] ?? { label: status, fg: '#475569', bg: '#f1f5f9' };
  return <Chip label={c.label} size="small" sx={{ color: c.fg, backgroundColor: c.bg, fontWeight: 700 }} />;
}

function fmt(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
}

function PanelBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ flex: 1, minWidth: 320, p: 2, border: '1px solid #e2e8f0', borderRadius: 2, backgroundColor: '#fff' }}>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}

/**
 * One insurance card. The image IS rendered: it is our own object in a private
 * bucket, reached through a URL minted for this response, and staff have to
 * look at it to compare insurer, policy number and expiry with what was typed.
 */
export function InsuranceCard({ policy, onDecided, showRunner = false }: {
  policy: RunnerInsurancePolicy;
  onDecided: (msg: string) => void;
  showRunner?: boolean;
}) {
  const caps = useCapabilities();
  const canReview = caps.has('runner-insurance:review');
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function decide(decision: 'approve' | 'reject') {
    setBusy(true);
    setError(null);
    try {
      const res = await adminApiClient.reviewRunnerInsurance(policy.id, { decision, reason: decision === 'reject' ? reason.trim() : undefined });
      const who = `${policy.runner.firstName} ${policy.runner.lastName}`;
      onDecided(decision === 'approve'
        ? `${who} — insurance approved${res.advancedTo ? ', and the application moved on to the handbook' : ''}.`
        : `${who} — insurance sent back with your note.`);
      setRejecting(false);
      setReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not go through.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box data-testid={`insurance-card-${policy.id}`} sx={{ borderTop: '1px solid #eef2f6', pt: 1.5, mt: 1 }}>
      {showRunner && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <Typography sx={{ fontWeight: 800 }}>{policy.runner.firstName} {policy.runner.lastName}</Typography>
          <Chip label={policy.runner.metro} size="small" />
          <Chip label={policy.runner.lifecycle} size="small" />
          {policy.runner.glpdId && <Chip label={policy.runner.glpdId} size="small" />}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {policy.cardViewUrl ? (
          <a href={policy.cardViewUrl} target="_blank" rel="noopener noreferrer" aria-label="Open the insurance card full size">
            <Box
              role="img"
              aria-label="Insurance card"
              sx={{ width: 240, height: 150, borderRadius: 1.5, border: '1px solid #e5e7eb', backgroundColor: '#f3f4f6',
                backgroundImage: `url(${policy.cardViewUrl})`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center' }}
            />
          </a>
        ) : (
          <Box sx={{ width: 240, height: 150, borderRadius: 1.5, backgroundColor: '#f3f4f6' }} />
        )}
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Box sx={{ mb: 0.5 }}><StatusChip status={policy.status} /></Box>
          <Typography variant="body2"><b>Insurer:</b> {policy.insurer}</Typography>
          <Typography variant="body2"><b>Policy number:</b> {policy.policyNumber}</Typography>
          <Typography variant="body2">
            <b>Expires:</b> {policy.expiresOn} {policy.daysLeft >= 0 ? `(${policy.daysLeft} days left)` : '(expired)'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Uploaded {fmt(policy.submittedAt)}</Typography>
          {policy.reviewedAt && <Typography variant="body2" sx={{ color: '#64748b' }}>Reviewed {fmt(policy.reviewedAt)}</Typography>}
          {policy.rejectReason && <Typography variant="body2" sx={{ color: '#b91c1c' }}>Sent back: {policy.rejectReason}</Typography>}
          {error && <Box sx={{ mt: 1 }}><Alert severity="error">{error}</Alert></Box>}
          {policy.status === 'pending' && canReview && (
            rejecting ? (
              <Box sx={{ mt: 1 }}>
                <TextField ariaLabel="Reason the Runner will read" label="Reason the Runner will read" size="small" fullWidth
                  value={reason} disabled={busy} onChange={(e) => setReason(e.target.value.slice(0, 500))}
                  placeholder="The expiry date on the card does not match what you typed." />
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button ariaLabel="Send the insurance card back" buttonText={busy ? 'Sending…' : 'Send it back'} size="small" variant="contained"
                    disabled={busy || !reason.trim()} sx={{ backgroundColor: '#b42318', '&:hover': { backgroundColor: '#912018' } }}
                    onClick={() => void decide('reject')} />
                  <Button ariaLabel="Cancel" buttonText="Cancel" size="small" variant="outlined" sx={NEUTRAL_BTN} disabled={busy}
                    onClick={() => { setRejecting(false); setReason(''); }} />
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button ariaLabel="Approve the insurance card" buttonText={busy ? 'Working…' : 'Approve'} size="small" variant="contained"
                  disabled={busy} onClick={() => void decide('approve')} />
                <Button ariaLabel="Send the insurance card back" buttonText="Send it back" size="small" variant="outlined" sx={NEUTRAL_BTN}
                  disabled={busy} onClick={() => setRejecting(true)} />
              </Box>
            )
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function RunnerInsurancePanel({ runnerId, onChanged }: { runnerId: string | null; onChanged: () => void }) {
  const [items, setItems] = useState<RunnerInsurancePolicy[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!runnerId) return;
    try {
      setItems((await adminApiClient.listRunnerInsurance({ status: 'all', runnerId, limit: 10 })).items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load insurance');
    }
  }, [runnerId]);
  useEffect(() => { void load(); }, [load]);

  return (
    <PanelBox title="Insurance">
      {!runnerId ? (
        <Typography variant="body2" sx={{ color: '#64748b' }}>Opens once the application is accepted and the profile exists.</Typography>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !items ? (
        <Spinner color="brand" />
      ) : !items.length ? (
        <Typography variant="body2" sx={{ color: '#64748b' }}>No card uploaded yet.</Typography>
      ) : (
        <>
          {flash && <Alert severity="success">{flash}</Alert>}
          {items.map((p) => (
            <InsuranceCard key={p.id} policy={p} onDecided={(m) => { setFlash(m); void load(); onChanged(); }} />
          ))}
        </>
      )}
    </PanelBox>
  );
}

const EVENT_LABEL: Record<string, string> = {
  booked: 'Booked', rescheduled: 'Rescheduled', canceled: 'Canceled', completed: 'Completed', no_show: 'No-show',
};

export function RunnerCallPanel({ applicationId, applicationStatus, onChanged }: {
  applicationId: string;
  applicationStatus: RunnerAppStatus;
  onChanged: () => void;
}) {
  const caps = useCapabilities();
  const canReview = caps.has('runner:review');
  const [calls, setCalls] = useState<RunnerVerificationCall[] | null>(null);
  const [events, setEvents] = useState<RunnerCallEvent[]>([]);
  const [interviewer, setInterviewer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await adminApiClient.listRunnerVerificationCalls({ status: 'all', applicationId, limit: 20 });
      setCalls(res.calls);
      setEvents(res.events ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the video call');
    }
  }, [applicationId]);
  useEffect(() => { void load(); }, [load]);

  const booked = calls?.find((c) => c.status === 'scheduled') ?? null;
  const counts = calls?.[0]?.counts;

  async function mark(outcome: 'complete' | 'no-show') {
    if (!booked) return;
    setBusy(true);
    setError(null);
    try {
      if (outcome === 'complete') await adminApiClient.completeRunnerVerificationCall(booked.id, { interviewerName: interviewer.trim() });
      else await adminApiClient.noShowRunnerVerificationCall(booked.id);
      setInterviewer('');
      await load();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not go through.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <PanelBox title="Video call">
      {error && <Box sx={{ mb: 1 }}><Alert severity="error">{error}</Alert></Box>}
      {!calls ? (
        <Spinner color="brand" />
      ) : (
        <>
          {counts && (
            <Typography variant="body2" sx={{ color: counts.rescheduled + counts.noShow > 1 ? '#b45309' : '#64748b', mb: 1 }}>
              Booked {counts.booked} · Rescheduled {counts.rescheduled} · Canceled {counts.canceled} · No-shows {counts.noShow}
            </Typography>
          )}
          {booked ? (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2"><b>Booked for</b> {fmt(booked.scheduledStart)}</Typography>
              {booked.joinUrl && (
                <Typography variant="body2">
                  <a href={booked.joinUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8' }}>Join the call ↗</a>
                </Typography>
              )}
              {canReview && applicationStatus === 'verification_call' && (
                <Box sx={{ mt: 1, p: 1.5, backgroundColor: '#f8fafc', borderRadius: 1.5 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Mark it completed only after you have seen the person match their photo ID and looked at their insurance card.
                    No copy of the ID is kept.
                  </Typography>
                  <TextField ariaLabel="Who held the call" label="Who held the call" size="small" fullWidth value={interviewer}
                    disabled={busy} onChange={(e) => setInterviewer(e.target.value)} />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button ariaLabel="Mark the video call completed" buttonText={busy ? 'Saving…' : 'Mark completed'} size="small"
                      variant="contained" disabled={busy || !interviewer.trim()} onClick={() => void mark('complete')} />
                    <Button ariaLabel="Mark the Runner as a no-show" buttonText="No-show" size="small" variant="outlined"
                      sx={NEUTRAL_BTN} disabled={busy} onClick={() => void mark('no-show')} />
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
              {calls.some((c) => c.status === 'completed')
                ? `Completed${calls.find((c) => c.status === 'completed')?.interviewerName ? ` by ${calls.find((c) => c.status === 'completed')!.interviewerName}` : ''}.`
                : applicationStatus === 'verification_call' ? 'Not booked yet. The Runner books it from their portal or app.' : 'Not booked.'}
            </Typography>
          )}
          {events.length > 0 && (
            <Box sx={{ borderTop: '1px solid #eef2f6', pt: 1 }}>
              {events.map((e) => (
                <Typography key={e.id} variant="body2" sx={{ color: '#64748b' }}>
                  {fmt(e.createdAt)} · {EVENT_LABEL[e.kind] ?? e.kind}
                </Typography>
              ))}
            </Box>
          )}
        </>
      )}
    </PanelBox>
  );
}
